import { useIonRouter } from "@ionic/react";
import React from "react";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleLinear, scaleBand } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { curveLinear } from "@visx/curve";

// Define the interface for therapy objects
export interface DailyTherapyData {
  value: number;
  color: string;
  target: number;
}
export interface OtherTherapiesInterface {
  name: string;
  value: number | string;
  unit: string;
  subText: string;
  target: number;
  isSpecial?: boolean;
  color?: string;
  visualType?: "bars" | "emoji" | "arrow" | string;
  dailyValues?: number[];
  dailyData?: DailyTherapyData[];
  dailyColors?: string[];
  dailyLabels?: string[] | number[];
  currentUser?: boolean; // Optional property to indicate if the therapy is special
}

const TreatmentRecapCard = ({
  currentUser, // Optional property to indicate if the user is logged in
  therapy,
  handleOnClick,
}: {
  currentUser?: boolean; // Optional property to indicate if the user is logged in
  therapy: OtherTherapiesInterface; // Allow therapy to be null for conditional rendering
  handleOnClick: (therapy: OtherTherapiesInterface) => void;
  // Indicates if the therapy is special
}) => {
  // Days of week for the progress bar with corresponding indexes
  const daysOfWeek = ["M", "T", "W", "T", "F", "S", "S"];
  const calculateTopPosition = (target: number): string => {
    // Start from a base position and adjust based on target
    // Higher targets appear higher (more negative top position)
    const positionMap = {
      1: "top-5",
      2: "top-4",
      3: "top-3",
      4: "top-2",
      5: "top-1",
      6: "top-0",
      7: "-top-1",
      8: "-top-2",
      9: "-top-3",
      10: "-top-4",
    };
    return positionMap[target as keyof typeof positionMap] || "top-1";
  };

  // Render arrow indicators for values
  const renderArrows = (therapy: OtherTherapiesInterface) => {
    const values = therapy.dailyValues || [1, 1, 1, 1, 1, 1, 1];
    const labels =
      therapy.dailyValues ||
      (therapy.name === "Heart Rate"
        ? Array(7).fill(therapy.value.toString())
        : Array(7).fill("?"));

    return (
      <div className="flex justify-between items-center mt-2 w-full">
        {daysOfWeek.map((day, index) => (
          <div key={index} className="flex flex-col items-center flex-1 mx-1">
            {/* Arrow icon - made responsive */}
            <svg
              className="w-[16px] h-[14px] md:w-5 md:h-5"
              viewBox="0 0 16 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.70711 0.292893C8.31658 -0.0976305 7.68342 -0.0976305 7.29289 0.292893L0.928933 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292893ZM9 14L9 1L7 1L7 14L9 14Z"
                fill="#B5B9BD"
              />
            </svg>

            {/* Value label - improved text scaling */}
            <span
              className="mt-1 text-xs sm:text-sm md:text-base"
              style={{ color: "#B5B9BD" }}
            >
              {labels[index]}
            </span>

            {/* Day label - improved text scaling */}
            <span className="mt-2 text-base" style={{ color: "rgb(84 88 93)" }}>
              {day}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Render emoji face indicators
  const renderEmojis = (therapy: OtherTherapiesInterface) => {
    const values = therapy.dailyValues || [1, 1, 1, 1, 1, 1, 1]; // Default mood values

    // Function to render the emotion face based on value
    const renderEmotionFace = (value: number) => {
      // Convert value to emotion type
      let emotionType;
      if (value === 1) emotionType = "very-sad";
      else if (value === 2) emotionType = "sad";
      else if (value === 3) emotionType = "neutral";
      else if (value === 4) emotionType = "happy";
      else if (value >= 5) emotionType = "very-happy";

      const color = therapy.color || "#0778CF"; // Brand blue color
      const strokeWidth = 2;

      // SVG Face Component
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 50 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Circle background */}
          <circle
            cx="25"
            cy="25"
            r="23"
            fill="white"
            stroke={color}
            strokeWidth={strokeWidth}
          />

          {/* Eyes and mouth based on emotion */}
          {emotionType === "very-sad" && (
            <>
              <ellipse
                cx="16"
                cy="19"
                rx="2"
                ry="3"
                transform="rotate(-90 16 19)"
                fill={color}
              />
              <ellipse
                cx="34"
                cy="19"
                rx="2"
                ry="3"
                transform="rotate(90 34 19)"
                fill={color}
              />
              <path
                d="M16 38C16 38 16 28 25 28C34 28 34 38 34 38"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            </>
          )}

          {emotionType === "sad" && (
            <>
              <circle cx="16" cy="20" r="3" fill={color} />
              <circle cx="34" cy="20" r="3" fill={color} />
              <path
                d="M34 37C34 37 34 31 25 31C16 31 16 37 16 37"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            </>
          )}

          {emotionType === "neutral" && (
            <>
              <circle cx="16" cy="20" r="3" fill={color} />
              <circle cx="34" cy="20" r="3" fill={color} />
              <path
                d="M34 35C34 35 32.5 35 25 35C17.5 35 16 35 16 35"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            </>
          )}

          {emotionType === "happy" && (
            <>
              <circle cx="16" cy="20" r="3" fill={color} />
              <circle cx="34" cy="20" r="3" fill={color} />
              <path
                d="M34 31C34 31 34 37 25 37C16 37 16 31 16 31"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            </>
          )}

          {emotionType === "very-happy" && (
            <>
              <ellipse cx="16" cy="19.5" rx="2" ry="4.5" fill={color} />
              <ellipse cx="34" cy="19.5" rx="2" ry="4.5" fill={color} />
              <path
                d="M34 31C34 31 34 41 25 41C16 41 16 31 16 31"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            </>
          )}
        </svg>
      );
    };

    return (
      <div className="flex biwaze-week-circles justify-between items-center w-[250px] mt-2">
        {daysOfWeek.map((day, index) => (
          <div key={index} className="flex flex-col items-center">
            {/* Emotion face */}
            {renderEmotionFace(values[index])}

            {/* Day label */}
            <span style={{ color: "rgb(84 88 93)", fontSize: "16px" }}>
              {day}
            </span>
          </div>
        ))}
      </div>
    );
  };

const renderWeekBars = (therapy: OtherTherapiesInterface) => {
  const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];

  const [activeBar, setActiveBar] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        setWidth((prev) => (Math.abs(prev - newWidth) > 5 ? newWidth : prev));
      }
    };
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, []);

  const isLargeScreen = width >= 480;
  const height = isLargeScreen ? 150 : 80;
  const margin = isLargeScreen 
    ? { top: 30, right: 5, bottom: 30, left: 40 }
    : { top: 20, right: 0, bottom: 24, left: 10 };
  
  const innerWidth = Math.max(width);
  const innerHeight = height - margin.top - margin.bottom;

  const values =
    therapy.dailyData?.map((d: DailyTherapyData) => d.value) || Array(7).fill(0);
  const colors =
    therapy.dailyData?.map((d: DailyTherapyData) => d.color) ||
    Array(7).fill(therapy.color || "#68C998");
  const targets =
    therapy.dailyData?.map((d: DailyTherapyData) => d.target) ||
    Array(7).fill(therapy.target || 0);

  const yMax = Math.max(...values, ...targets, 1) * 1.2;

  const xScale = scaleBand({
    domain: Array.from({ length: 7 }, (_, i) => i),
    range: [0, innerWidth],
    padding: isLargeScreen ? 0.5 : 0.4,
  });

  const yScale = scaleLinear({
    domain: [0, yMax],
    range: [innerHeight, 0],
  });

  const barWidth = xScale.bandwidth();
  const tooltipWidth = isLargeScreen 
    ? Math.min(Math.max(barWidth * 1.2, 90), 130)
    : Math.min(Math.max(barWidth * 1.5, 70), 110);

  if (width === 0)
    return <div ref={containerRef} style={{ width: "100%", height }} />;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          <LinePath
            data={Array.from({ length: 7 }, (_, i) => i)}
            x={(i) => (xScale(i) || 0) + barWidth / 2}
            y={(i) => yScale(targets[i])}
            stroke="#60A5FA"
            strokeWidth={isLargeScreen ? 2 : 1.5}
            strokeDasharray="4,4"
            curve={curveLinear}
          />

          {values.map((value, i) => {
            const barX = xScale(i) || 0;
            const barY = yScale(value);
            const barHeight = Math.max(innerHeight - barY, 2);

            return (
              <g key={`bar-${i}`} 
                  onMouseEnter={() => setActiveBar(i)}
                  onMouseLeave={() => setActiveBar(null)}>
                <Bar
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={colors[i]}
                  rx={isLargeScreen ? 4 : 2}
                  style={{ cursor: "pointer" }}
                />

                <text
                  x={barX + barWidth / 2}
                  y={innerHeight + (isLargeScreen ? 20 : 16)}
                  textAnchor="middle"
                  fontSize={isLargeScreen ? 13 : 11}
                  fontWeight={500}
                  fill="#6B7280"
                >
                  {dayLabels[i]}
                </text>
              </g>
            );
          })}
        </Group>
      </svg>

      {activeBar !== null && (
        <div
          style={{
            position: "absolute",
            left: `${margin.left + (xScale(activeBar) || 0) + barWidth / 2}px`,
            top: `${margin.top + yScale(values[activeBar]) - (isLargeScreen ? 44 : 38)}px`,
            transform: "translateX(-50%)",
            minWidth: `${tooltipWidth}px`,
            height: isLargeScreen ? "36px" : "32px",
            background: "rgba(0, 0, 0, 0.9)",
            borderRadius: isLargeScreen ? "8px" : "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1000,
            padding: isLargeScreen ? "0 12px" : "0 8px",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              color: "white",
              fontSize: isLargeScreen ? "14px" : "13px",
              fontWeight: 600,
            }}
          >
            {`${values[activeBar]} / ${targets[activeBar]}`}
          </span>
        </div>
      )}
    </div>
  );
};

  const isSpecialTherapy =
    therapy.isSpecial ||
    therapy.name === "Vest treatments" ||
    therapy.name === "Breathing routines";
  const TherapyHeader = () => {
    const router = useIonRouter();
    // Check if this therapy is a special therapy based on the isSpecial prop or name
    return (
      <>
        <h2
          className="text-xl font-medium"
          style={{
            color: isSpecialTherapy && currentUser ? "#0778CF" : "#504f4e",
          }}
        >
          {therapy.name}
        </h2>

        {isSpecialTherapy && currentUser && (
          <>
            {therapy?.unit !== "" ? (
              <button
                className="w-auto h-8 rounded-full flex items-center justify-center border-0"
                style={{ color: "#05ACD9" }}
                aria-label="Add therapy"
                title="Add therapy"
                onClick={() => handleOnClick(therapy)} // Pass therapy data on click
              >
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.53809 18.6191C8.31348 18.6191 7.15918 18.3848 6.0752 17.916C4.99707 17.4473 4.04492 16.7998 3.21875 15.9736C2.39258 15.1475 1.74512 14.1953 1.27637 13.1172C0.807617 12.0332 0.573242 10.8789 0.573242 9.6543C0.573242 8.42969 0.807617 7.27832 1.27637 6.2002C1.74512 5.11621 2.38965 4.16113 3.20996 3.33496C4.03613 2.50879 4.98828 1.86133 6.06641 1.39258C7.15039 0.923828 8.30469 0.689453 9.5293 0.689453C10.7539 0.689453 11.9082 0.923828 12.9922 1.39258C14.0762 1.86133 15.0312 2.50879 15.8574 3.33496C16.6836 4.16113 17.3311 5.11621 17.7998 6.2002C18.2686 7.27832 18.5029 8.42969 18.5029 9.6543C18.5029 10.8789 18.2686 12.0332 17.7998 13.1172C17.3311 14.1953 16.6836 15.1475 15.8574 15.9736C15.0312 16.7998 14.0762 17.4473 12.9922 17.916C11.9141 18.3848 10.7627 18.6191 9.53809 18.6191ZM9.53809 17.125C10.5752 17.125 11.5449 16.9316 12.4473 16.5449C13.3496 16.1582 14.1436 15.625 14.8291 14.9453C15.5146 14.2598 16.0479 13.4658 16.4287 12.5635C16.8154 11.6611 17.0088 10.6914 17.0088 9.6543C17.0088 8.61719 16.8154 7.64746 16.4287 6.74512C16.042 5.84277 15.5059 5.04883 14.8203 4.36328C14.1406 3.67773 13.3467 3.14453 12.4385 2.76367C11.5361 2.37695 10.5664 2.18359 9.5293 2.18359C8.49219 2.18359 7.52246 2.37695 6.62012 2.76367C5.71777 3.14453 4.92676 3.67773 4.24707 4.36328C3.56738 5.04883 3.03418 5.84277 2.64746 6.74512C2.2666 7.64746 2.07617 8.61719 2.07617 9.6543C2.07617 10.6914 2.2666 11.6611 2.64746 12.5635C3.03418 13.4658 3.56738 14.2598 4.24707 14.9453C4.93262 15.625 5.72656 16.1582 6.62891 16.5449C7.53125 16.9316 8.50098 17.125 9.53809 17.125ZM5.45996 9.6543C5.45996 9.43164 5.52734 9.25293 5.66211 9.11816C5.80273 8.97754 5.9873 8.90723 6.21582 8.90723H8.7998V6.32324C8.7998 6.10059 8.86719 5.91895 9.00195 5.77832C9.13672 5.6377 9.30957 5.56738 9.52051 5.56738C9.74316 5.56738 9.92188 5.6377 10.0566 5.77832C10.1973 5.91309 10.2676 6.09473 10.2676 6.32324V8.90723H12.8604C13.083 8.90723 13.2617 8.97754 13.3965 9.11816C13.5371 9.25293 13.6074 9.43164 13.6074 9.6543C13.6074 9.86523 13.5371 10.0381 13.3965 10.1729C13.2617 10.3076 13.083 10.375 12.8604 10.375H10.2676V12.9678C10.2676 13.1904 10.1973 13.3721 10.0566 13.5127C9.92188 13.6475 9.74316 13.7148 9.52051 13.7148C9.30957 13.7148 9.13672 13.6475 9.00195 13.5127C8.86719 13.3721 8.7998 13.1904 8.7998 12.9678V10.375H6.21582C5.99316 10.375 5.81152 10.3076 5.6709 10.1729C5.53027 10.0381 5.45996 9.86523 5.45996 9.6543Z"
                    fill="#05ACD9"
                  />
                </svg>{" "}
              </button>
            ) : (
              <span
                style={{ cursor: "pointer", color: "#05ACD9" }}
                onClick={() => router.push("/settings/edit-goals")}
              >
                Set Goal
              </span>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div
      className="biwaze-therapy-item bg-white rounded-2xl shadow-lg my-2"
      style={{
        width: "100%",
        boxShadow: isSpecialTherapy ? "0px 0px 20px #3A285033" : "",
        borderRadius: "15px",
        padding: "12px 20px",
      }}
    >
      {/* Header */}
      <div className="biwaze-therapy-name flex justify-between w-full">
        <TherapyHeader />
      </div>

      {/* Content */}
      <div className="biwaze-therapy-content mt-2">
        <div className="biwaze-therapy-details">
          <div className="flex items-baseline">
            <span
              className={`biwaze-therapy-value pb-2 font-bold ${
                typeof therapy.value === "string"
                  ? "text-3xl max-w-[250px]"
                  : "text-5xl"
              }`}
              style={{
                fontSize: "24px",
                lineHeight: "1",
                color: "#504f4e",
                overflow:
                  typeof therapy.value === "string" ? "hidden" : "visible",
                textOverflow:
                  typeof therapy.value === "string" ? "ellipsis" : "initial",
                whiteSpace:
                  typeof therapy.value === "string" ? "nowrap" : "normal",
                display: "inline-block",
              }}
            >
              {therapy.value}
            </span>
            <span className="biwaze-therapy-unit ml-1">
              {therapy.unit && therapy.unit !== "" ? " of" : ""}
              <span style={{ color: "" }}>
                {" "}
                {therapy.unit?.replace("of", "")}
              </span>
            </span>
          </div>
          <div
            className="biwaze-therapy-date"
            style={{
              color: "#504f4e",
            }}
          >
            {therapy.subText}
          </div>
        </div>

        {/* Week Indicators based on visualType */}
        <div className="biwaze-week-circles flex">
          {therapy.visualType === "emoji"
            ? renderEmojis(therapy)
            : therapy.visualType === "arrow"
            ? renderArrows(therapy)
            : renderWeekBars(therapy)}
        </div>
      </div>
    </div>
  );
};

export default TreatmentRecapCard;
